/**
 * 2026-04-30 — tiny inline price-history sparkline for MarketCard.
 *
 * SVG-only, no recharts/d3 dependency. Renders an ascending series of
 * integer-cents points as a smooth line, with a faint area fill in
 * the same color. Color follows the trend: green if last >= first,
 * red otherwise — Polymarket-style cue without a separate legend.
 *
 * The component is server-renderable (no useState, no useEffect),
 * which keeps MarketCard a server component.
 */

interface SparklineProps {
  /** Integer cents, oldest → newest. ≥2 points required to render. */
  points: number[] | null;
  /** Pixel width. Defaults to 80 to fit the MarketCard layout. */
  width?: number;
  /** Pixel height. Defaults to 20. */
  height?: number;
  /** ARIA label override. Default: "Last 7 days price trend". */
  ariaLabel?: string;
}

export function Sparkline({
  points,
  width = 80,
  height = 20,
  ariaLabel = "Last 7 days price trend",
}: SparklineProps) {
  if (!points || points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  // Avoid divide-by-zero when every point equals (flat line).
  const yRange = Math.max(1, max - min);
  const xStep = width / (points.length - 1);

  // Padding so the stroke doesn't clip on flat top/bottom lines.
  const PAD = 1.5;
  const usable = height - PAD * 2;

  const coords = points.map((p, i) => {
    const x = i * xStep;
    const y = PAD + (1 - (p - min) / yRange) * usable;
    return { x, y };
  });

  const linePath = coords
    .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(" ");

  const areaPath =
    `${linePath} L${(width).toFixed(2)},${height} L0,${height} Z`;

  const first = points[0];
  const last = points[points.length - 1];
  const trendUp = last >= first;
  const stroke = trendUp ? "var(--color-yes)" : "var(--color-no)";
  // Trend description for screen readers — the visual cue (green vs
  // red) is invisible to assistive tech, so we serialize the actual
  // delta into a hidden <title> + <desc>. Picked up by VoiceOver /
  // NVDA / TalkBack via role=img + aria-labelledby semantics.
  const deltaCents = last - first;
  const deltaPct = first === 0 ? 0 : (deltaCents / first) * 100;
  const deltaDescriptor =
    deltaCents === 0
      ? "flat"
      : trendUp
        ? `up ${deltaCents}¢ (${deltaPct.toFixed(1)}%)`
        : `down ${Math.abs(deltaCents)}¢ (${Math.abs(deltaPct).toFixed(1)}%)`;
  const accessibleDescription = `${ariaLabel}: ${first}¢ → ${last}¢, ${deltaDescriptor}.`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={accessibleDescription}
      className="overflow-visible"
    >
      <title>{accessibleDescription}</title>
      <path
        d={areaPath}
        fill={stroke}
        opacity={0.12}
        stroke="none"
      />
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
